import { autoUpdate, computePosition, flip, offset, shift } from "@floating-ui/dom";
import { Requester } from "../common/requester";
import { TimesheetRightTableColumn } from "./timesheet-models";
import { C } from "../common/component";

class CommentPopup
{
    private readonly cell;
    private comment: string;
    private readonly save: (comment: string) => Promise<void>;

    private tooltipElement;
    private textareaElement;
    private cleanupAutoUpdate;
    private hasFocus = false;

    constructor(cell, comment: string, save: (comment: string) => Promise<void>)
    {
        this.cell = cell;
        this.comment = comment;
        this.save = save;

        if (this.comment)
        {
            this.cell.addClass("timesheetCommentCell");
        }
    }

    remove()
    {
        this.tooltipElement?.remove();
        this.tooltipElement = null;
        this.textareaElement = null;

        if (this.cleanupAutoUpdate)
        {
            this.cleanupAutoUpdate();
            this.cleanupAutoUpdate = null;
        }
    }

    open(focus: boolean)
    {
        const input = C.textarea
            .val(this.comment)
            .attr("rows", "5")
            .css({
                resize: "none",
                width: "200px"
            });

        this.textareaElement = input;

        this.tooltipElement = C.div
            .css({
                "position": "absolute",
                "width": "200px",
                "top": "0",
                "left": "0"
            })
            .append(input)
            .appendTo(C.body);

        this.preventHOTInterference(input);
        this.preventHOTInterference(this.tooltipElement);

        this.cleanupAutoUpdate = autoUpdate(this.cell[0], this.tooltipElement[0], () =>
        {
            computePosition(this.cell[0], this.tooltipElement[0], {
                placement: "right-start",
                middleware: [flip(), offset(4), shift()]
            }).then(({ x, y }) =>
            {
                this.tooltipElement[0].style.left = `${x}px`;
                this.tooltipElement[0].style.top = `${y}px`;
            });
        });

        input.on("focus", () => this.hasFocus = true);

        input.on("blur", async () =>
        {
            if (!this.hasFocus)
            {
                return;
            }

            const newComment = input.val() as string;
            await this.save(newComment);
            this.comment = newComment;

            if (newComment)
            {
                this.cell.addClass("timesheetCommentCell");
            }
            else
            {
                this.cell.removeClass("timesheetCommentCell");
            }

            this.hasFocus = false;
            this.tooltipElement.css("display", "none");
        });

        input.on("keydown", (e) =>
        {
            if (e.key === "Escape")
            {
                e.stopPropagation();
                this.tooltipElement.css("display", "none");
                this.hasFocus = false;
            }
        });

        document.addEventListener("mousedown", this.handleOutsideClick, true);

        if (focus)
        {
            // setTimeout is needed because otherwise browser will scroll up
            // (because initially input is in the beginning of page, so we need to focus it after it has been set to right place)
            setTimeout(() =>
            {
                input[0].focus();
                const el = input[0] as HTMLTextAreaElement;
                const val = input.val()?.toString() ?? "";
                el.setSelectionRange(val.length, val.length);
            }, 0);
        }
    }

    private preventHOTInterference(el: JQuery)
    {
        el.on("mousedown mouseup click keydown keypress keyup input", (e) =>
        {
            e.stopImmediatePropagation();
            e.stopPropagation();
        });
    }

    private handleOutsideClick = (e: MouseEvent) =>
    {
        if (!this.tooltipElement)
        {
            return;
        }

        if (!this.tooltipElement[0].contains(e.target as Node))
        {
            this.textareaElement.trigger("blur");
            document.removeEventListener("mousedown", this.handleOutsideClick, true);
        }
    };
}

export class TimesheetCommentsManager
{
    private readonly columns: TimesheetRightTableColumn[];
    private readonly rows: string[];
    private readonly comments: { [rowId: string]: { [date: string]: string } };
    private readonly commentSaveUrl: string;
    private readonly editableComments: string[];

    private readonly commentPopups: { [rowNumber: number]: { [columnNumber: number]: CommentPopup } } = {};

    constructor(columns: TimesheetRightTableColumn[], rows: string[],
                comments: { [rowId: string]: { [date: string]: string } }, commentSaveUrl: string,
                editableComments: string[])
    {
        this.columns = columns;
        this.rows = rows;
        this.comments = comments;
        this.commentSaveUrl = commentSaveUrl;
        this.editableComments = editableComments;
        for (let i = 0; i < rows.length; i++)
        {
            this.commentPopups[i] = {};
        }
    }

    configureComment(cell, rowNumber: number, columnNumber: number)
    {
        this.addPopup(cell, rowNumber, columnNumber, false);
    }

    openNewComment(cell, rowNumber: number, columnNumber: number)
    {
        this.addPopup(cell, rowNumber, columnNumber, true).open(true);
    }

    private addPopup(cell, rowNumber: number, columnNumber: number, addEvenIfNoComment: boolean): CommentPopup
    {
        const comment = this.getComment(rowNumber, columnNumber);
        const popup = addEvenIfNoComment || comment
            ? new CommentPopup(cell, comment, x => this.saveComment(rowNumber, columnNumber, x))
            : null;
        const oldPopup = this.commentPopups[rowNumber][columnNumber];
        this.commentPopups[rowNumber][columnNumber] = popup;
        if (oldPopup)
        {
            oldPopup.remove();
        }
        return popup;
    }

    getComment(rowNumber: number, columnNumber: number)
    {
        const rowComments = this.comments[this.rows[rowNumber]];
        if (rowComments)
        {
            const date = columnNumber == null ? "" : this.columns[columnNumber].Date;
            return rowComments[date];
        }
        return null;
    }

    isCommentEditable(rowNumber: number): boolean
    {
        return this.editableComments.includes(this.rows[rowNumber]);
    }

    async saveComment(rowNumber: number, columnNumber: number, comment: string): Promise<void>
    {
        const date = columnNumber == null ? "" : this.columns[columnNumber].Date;
        const row = this.rows[rowNumber];
        this.comments[row][date] = comment;
        await Requester.post(
            Requester.getUrl(this.commentSaveUrl, { date: date, row: row }),
            comment, { dataType: null }
        );
    }
}
